/**
 * Data-integration helpers for restoring backups WITHOUT losing records.
 *
 * Instead of wiping a collection and replacing it with the backup (which loses
 * anything created on another device), we UPSERT by `id` and resolve conflicts
 * with LAST-WRITE-WINS using the record's own timestamps:
 *   _last_updated_at  (stamped by AutoSync on every edit)
 *   -> updated_at / updatedAt
 *   -> created_at / createdAt
 *
 * Result: records unique to either side are kept; when both sides have the same
 * id, the newer one wins; a tie keeps the local copy (avoids needless churn).
 */

export function recordTimestamp(item) {
  if (!item || typeof item !== 'object') return 0;
  const raw =
    item._last_updated_at ||
    item.updated_at ||
    item.updatedAt ||
    item.created_at ||
    item.createdAt ||
    0;
  if (typeof raw === 'number') return raw;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * Merge two arrays of records by id with last-write-wins conflict resolution.
 * @param {Array} existing current local records
 * @param {Array} incoming records from the backup / other device
 * @returns {{ merged: Array, added: number, updated: number, kept: number }}
 */
export function mergeRecordArrays(existing = [], incoming = []) {
  const byId = new Map();
  let added = 0;
  let updated = 0;

  (Array.isArray(existing) ? existing : []).forEach((it) => {
    if (it && it.id != null) byId.set(it.id, it);
  });

  (Array.isArray(incoming) ? incoming : []).forEach((it) => {
    if (!it || it.id == null) return;
    const current = byId.get(it.id);
    if (!current) {
      byId.set(it.id, it);
      added += 1;
      return;
    }
    // Conflict: keep whichever was last written. Tie -> keep local.
    if (recordTimestamp(it) > recordTimestamp(current)) {
      byId.set(it.id, it);
      updated += 1;
    }
  });

  const merged = [...byId.values()];
  return { merged, added, updated, kept: merged.length - added };
}

/**
 * Settings is a single object, not a list. Shallow-merge so new keys fill in
 * and changed keys from the backup win, without dropping keys only present
 * locally.
 */
export function mergeSettings(existing = {}, incoming = {}) {
  return { ...(existing || {}), ...(incoming || {}) };
}

export default mergeRecordArrays;
